/**
 * @file UsStatesTerritories.js
 * @description A dropdown component for selecting a US state or territory.
 */

import React from "react";
import { getUsStatesTerritories } from "../../../utils/utils";
import { Dropdown } from "../../helpers/Dropdown";
import { getText } from '../../../i18n';

/**
 * A dropdown component for selecting US states and territories.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - Additional CSS class for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines if the label should be displayed inline.
 * @param {string} props.selectedState - The currently selected state.
 * @param {function} props.onStateChange - The callback function to be called when the state selection changes.
 * @param {string} [props.label="State"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines if the dropdown is required.
 * @returns {JSX.Element} The rendered component.
 */
const UsStatesTerritories = ({
  className,
  inlineLabel = true,
  selectedState,
  onStateChange,
  label = "State",
  isRequired = false,
}) => {
  const usStatesTerritories = getUsStatesTerritories();

  const getDefaultLabel = () => {
    const matchingState = usStatesTerritories.find(
      (state) => state.acronym === selectedState
    );
    return matchingState ? matchingState.name : "Select State";
  };

  const stateOptions = usStatesTerritories.map((state) => ({
    value: state.acronym,
    label: state.name,
  }));

  if (!isRequired)
    stateOptions.unshift({ value: "", label: getText('msg_1bcf1fee269d') });

  return (
    <div
      className={`form-group mb-3 ${inlineLabel ? "inline-input" : ""
        } ${className}`}
    >
      <label htmlFor="us-states-territories-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <Dropdown
        id="us-states-territories-dropdown"
        defaultValue={getDefaultLabel()}
        options={stateOptions}
        value={selectedState}
        onChange={onStateChange}
      />
    </div>
  );
};

export default UsStatesTerritories;
